import React, { FormEvent, memo, useState } from 'react';
import { createBrandTrial } from './api';
import { markBrandTrialClaimPending } from './brandTrialClaim';
import { BRAND_LANDING_COPY } from './copy';
import { getQpDomain } from 'utils/appOrigin';

type Props = {
  id?: string;
};

type Step = 'brand' | 'email';

function normalizeWebsite(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return trimmed;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

function extractErrorMessage(err: unknown): string {
  const anyErr = err as {
    response?: {
      data?: {
        message?: string | string[] | { message?: string };
        error?: string;
      };
    };
    message?: string;
  };
  const msg = anyErr?.response?.data?.message;
  if (Array.isArray(msg)) return msg.join(', ');
  if (typeof msg === 'string' && msg.trim()) return msg;
  if (msg && typeof msg === 'object' && typeof msg.message === 'string') {
    return msg.message;
  }
  if (anyErr?.response?.data?.error) return String(anyErr.response.data.error);
  if (anyErr?.message) return anyErr.message;
  return 'Something went wrong. Please try again.';
}

function buildOAuthUrl(): string {
  const apiUrl = process.env.REACT_APP_API_URL || '';
  const appOrigin = getQpDomain();
  const urlToRedirect = `${window.location.origin}/ai-audit?claimBrandTrial=1`;
  return `${apiUrl}/auth/oauth?appOrigin=${encodeURIComponent(
    appOrigin,
  )}&urlToRedirect=${encodeURIComponent(urlToRedirect)}`;
}

function TryBrandForm({ id = 'signup' }: Props) {
  const c = BRAND_LANDING_COPY;
  const [step, setStep] = useState<Step>('brand');
  const [brandName, setBrandName] = useState('');
  const [website, setWebsite] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [ready, setReady] = useState(false);
  const [accountStatus, setAccountStatus] = useState<'created' | 'existing'>(
    'created',
  );

  function onContinueToEmail(e: FormEvent) {
    e.preventDefault();
    setError('');
    const brand = brandName.trim();
    const domain = website.trim();
    if (!brand || !domain) {
      setError('Please fill in brand name and website.');
      return;
    }
    setStep('email');
  }

  async function onStartAudit(e: FormEvent) {
    e.preventDefault();
    setError('');
    const brand = brandName.trim();
    const domain = website.trim();
    const mail = email.trim();
    if (!mail) {
      setError('Enter a valid work email.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(mail)) {
      setError('Enter a valid work email.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await createBrandTrial({
        brandName: brand,
        brandDomain: normalizeWebsite(domain),
        email: mail,
      });
      setAccountStatus(
        res.data?.accountStatus === 'existing' ? 'existing' : 'created',
      );
      setReady(true);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  function continueWithQuestionPro() {
    markBrandTrialClaimPending();
    window.location.href = buildOAuthUrl();
  }

  if (ready) {
    const successBody =
      accountStatus === 'existing'
        ? c.formSuccessBodyExisting
        : c.formSuccessBody;
    return (
      <div className="brand-landing-form brand-landing-form__success" id={id}>
        <h3>{c.formSuccessTitle}</h3>
        <p>{successBody}</p>
        <button
          type="button"
          className="brand-landing__btn"
          onClick={continueWithQuestionPro}
          data-testid="brand-trial-oauth-cta"
        >
          {c.formContinueOAuth}
        </button>
      </div>
    );
  }

  if (step === 'email') {
    return (
      <form
        className="brand-landing-form"
        id={id}
        onSubmit={onStartAudit}
        noValidate
        data-testid="brand-trial-step-email"
      >
        <p className="brand-landing-form__summary">
          <strong>{brandName.trim()}</strong>
          <span aria-hidden="true"> · </span>
          <span>{website.trim()}</span>
        </p>

        <label htmlFor="brand-trial-email">{c.formEmailLabel}</label>
        <input
          id="brand-trial-email"
          name="email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <p className="brand-landing-form__hint">{c.formEmailHint}</p>

        {error ? (
          <p className="brand-landing-form__error" role="alert">
            {error}
          </p>
        ) : null}

        <button type="submit" className="brand-landing__btn" disabled={submitting}>
          {submitting ? 'Starting…' : c.formStartAudit}
        </button>
        <button
          type="button"
          className="brand-landing-form__back"
          onClick={() => {
            setError('');
            setStep('brand');
          }}
        >
          {c.formBack}
        </button>
      </form>
    );
  }

  return (
    <form
      className="brand-landing-form"
      id={id}
      onSubmit={onContinueToEmail}
      noValidate
      data-testid="brand-trial-step-brand"
    >
      <label htmlFor="brand-trial-brand">{c.formBrandLabel}</label>
      <input
        id="brand-trial-brand"
        name="brandName"
        autoComplete="organization"
        value={brandName}
        onChange={(e) => setBrandName(e.target.value)}
        required
      />

      <label htmlFor="brand-trial-website">{c.formWebsiteLabel}</label>
      <input
        id="brand-trial-website"
        name="brandDomain"
        autoComplete="url"
        placeholder="https://www.example.com"
        value={website}
        onChange={(e) => setWebsite(e.target.value)}
        required
      />

      {error ? (
        <p className="brand-landing-form__error" role="alert">
          {error}
        </p>
      ) : null}

      <button type="submit" className="brand-landing__btn">
        {c.formContinue}
      </button>
    </form>
  );
}

export default memo(TryBrandForm);
